import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  AdditionalCareParticipantsFeatureConfigurationResponse,
  AdmissionCategoryMappingConfigurationResponse,
  AuthStatusAttachmentsTransitionConfigurationResponse,
  AutomatedDenialLetterConfigurationResponse,
  FacilityBasedRequestConfigurationResponse,
  LettersExpansionConfigurationResponse,
  LevelOfCareMappingConfigurationResponse,
  NoteConfigurationResponse,
  OtherInsuranceConfigurationResponse,
  OutOfNetworkReviewConfigurationResponse,
  PostDecisionConfigurationResponse,
  useGetFeatureConfiguration,
  StatusTransitionConfigurationResponse,
  AcuityLevelConfigurationResponse,
  DecisionAppealsConfigurationResponse,
  PostDecisionReconsiderationsConfigurationResponse,
  RequestStatusConfiguration,
  ReviewConfigurationResponse,
  TinConfigurationResponse,
  CaseHoldConfigurationResponse,
  CorePlatformIntegrationConfigurationResponse,
  PatientStayRangesConfiguration,
  NonPalCheckboxConfigurationResponse,
  SalesforceIntegrationConfigurationResponse,
  RecentlyUsedProviderConfiguration,
  ReviewChatbotConfigurationResponse,
  P2PIntegratedSchedulingConfiguration,
  ManuallyCreatedProvidersConfiguration,
  NotInContractConfiguration,
  ReviewNoteConfigurationResponse,
  TransferAuthorizationConfigurationResponse,
  NetworkServiceConfigurationResponse,
  FeatureFlagConfigurationResponse,
  ServiceRequestWithdrawalConfigurationResponse,
  TemporaryMemberAuthsConfigurationResponse,
  ContinuationConfigurationResponse,
  RetroRequestConfigurationResponse,
  ContactInfoConfigurationResponse,
  MissingInformationOutreachConfigurationResponse,
  QueueManagementConfigurationResponse,
  SubmissionConfigurationResponse,
  MemberLookupConfigurationResponse,
  EpisodeUnitsConfigurationResponse,
} from "@coherehealth/core-platform-api";
import { debounce, DebouncedFuncLeading } from "lodash";

interface Logger {
  warn: (...args: any[]) => void | undefined;
  error: (ex: any) => void | undefined;
}

interface FeatureConfigurationsRecord {
  additionalCareParticipantsConfiguration?: AdditionalCareParticipantsFeatureConfigurationResponse;
  contactInfoConfiguration?: ContactInfoConfigurationResponse;
  admissionCategoryMappingConfiguration?: AdmissionCategoryMappingConfigurationResponse;
  caseHoldConfiguration?: CaseHoldConfigurationResponse;
  levelOfCareMappingConfiguration?: LevelOfCareMappingConfigurationResponse;
  facilityBasedRequestConfiguration?: FacilityBasedRequestConfigurationResponse;
  postDecisionConfiguration?: PostDecisionConfigurationResponse;
  automatedDenialLetterConfiguration?: AutomatedDenialLetterConfigurationResponse;
  authStatusAttachmentsTransitionConfiguration?: AuthStatusAttachmentsTransitionConfigurationResponse;
  lettersExpansionConfiguration?: LettersExpansionConfigurationResponse;
  otherInsuranceConfiguration?: OtherInsuranceConfigurationResponse;
  outOfNetworkReviewConfiguration?: OutOfNetworkReviewConfigurationResponse;
  reviewConfiguration?: ReviewConfigurationResponse;
  statusTransitionConfiguration?: StatusTransitionConfigurationResponse;
  acuityLevelConfiguration?: AcuityLevelConfigurationResponse;
  noteConfiguration?: NoteConfigurationResponse;
  decisionAppealsConfiguration?: DecisionAppealsConfigurationResponse;
  postDecisionRedeterminationsConfiguration?: PostDecisionReconsiderationsConfigurationResponse;
  requestStatusConfiguration?: RequestStatusConfiguration;
  tinConfiguration?: TinConfigurationResponse;
  corePlatformIntegrationConfiguration?: CorePlatformIntegrationConfigurationResponse;
  patientStayRangesConfiguration?: PatientStayRangesConfiguration;
  nonPalCheckboxConfiguration?: NonPalCheckboxConfigurationResponse;
  salesforceConfiguration?: SalesforceIntegrationConfigurationResponse;
  recentlyUsedProviderConfiguration?: RecentlyUsedProviderConfiguration;
  reviewChatbotConfiguration?: ReviewChatbotConfigurationResponse;
  P2PIntegratedSchedulingConfiguration?: P2PIntegratedSchedulingConfiguration;
  manuallyCreatedProvidersConfiguration?: ManuallyCreatedProvidersConfiguration;
  NotInContractConfiguration?: NotInContractConfiguration;
  reviewNoteConfiguration?: ReviewNoteConfigurationResponse;
  temporaryMemberAuthsConfiguration?: TemporaryMemberAuthsConfigurationResponse;
  transferAuthorizationConfiguration?: TransferAuthorizationConfigurationResponse;
  serviceRequestWithdrawalConfiguration?: ServiceRequestWithdrawalConfigurationResponse;
  networkServiceConfiguration?: NetworkServiceConfigurationResponse;
  featureFlagConfiguration?: FeatureFlagConfigurationResponse;
  continuationConfiguration?: ContinuationConfigurationResponse;
  retroRequestConfiguration?: RetroRequestConfigurationResponse;
  missingInformationOutreachConfiguration?: MissingInformationOutreachConfigurationResponse;
  queueManagementConfiguration?: QueueManagementConfigurationResponse;
  submissionConfiguration?: SubmissionConfigurationResponse;
  memberLookupConfiguration?: MemberLookupConfigurationResponse;
  episodeUnitsConfiguration?: EpisodeUnitsConfigurationResponse;
}
type FeatureConfiguration = keyof FeatureConfigurationsRecord;

type FetchFeatureConfigurationReturnType<T extends FeatureConfiguration> = FeatureConfigurationsRecord[T];

type HealthPlanNameAndDelegatedVendorAndFeatureConfigurationsRecord = Record<
  string,
  FeatureConfigurationsRecord | undefined
>;

/**
 * Feature configs exist at the health plan level, or at the health plan -> delegated vendor sublevel.
 * To form a location (configurationsKey) to store the configs, we join plan + vendor using `::`.
 */
const initialFeatureConfigurationRecord: HealthPlanNameAndDelegatedVendorAndFeatureConfigurationsRecord = {
  /*
  // Health plan level configs, of the form
  Humana: undefined
  */
  /*
  // Delegated vendor level configs, of the form
  [`Humana::Oak Street Health`]: undefined,
  */
};

interface FeatureConfigurationContextType {
  featureConfigurations: HealthPlanNameAndDelegatedVendorAndFeatureConfigurationsRecord;
  fetchFeatureConfiguration?: <T extends FeatureConfiguration>(
    configuration: T,
    healthPlanName: string,
    delegatedVendorName?: string
  ) => Promise<FetchFeatureConfigurationReturnType<T> | undefined>;
}

export const FeatureConfigurationContext = createContext<FeatureConfigurationContextType>({
  featureConfigurations: initialFeatureConfigurationRecord,
});

interface FeatureConfigurationProviderProps {
  getAccessToken: () => Promise<string | undefined>;
  logger: Logger;
  children: React.ReactNode;
}

/**
 *  Feature Configuration Provider should be place on the top hierarchy of the components.
 *  If you are using new Feature Configuration add it to the record + include useGetFacilityBasedRequestConfiguration in component below.
 *  Additionally add the call to fetchFeatureConfiguration method so it fetches the configuration.
 */
export const FeatureConfigurationProvider = ({
  getAccessToken,
  logger,
  children,
}: FeatureConfigurationProviderProps) => {
  const { warn: logWarning } = logger;
  const [featureConfigurationsRecord, setFeatureConfigurationRecord] =
    useState<HealthPlanNameAndDelegatedVendorAndFeatureConfigurationsRecord>(initialFeatureConfigurationRecord);

  const { mutate: fetchConfiguration } = useGetFeatureConfiguration({
    configuration: "none",
    queryParams: {
      featureConfigurationLevel: "HEALTH_PLAN",
      healthPlanName: "none",
    },
    base: process.env.REACT_APP_API_URL,
  });

  const fetchFeatureConfiguration = async <T extends FeatureConfiguration>(
    configuration: T,
    healthPlanName: string,
    delegatedVendorName?: string
  ): Promise<FetchFeatureConfigurationReturnType<T> | undefined> => {
    try {
      const accessToken = await getAccessToken();
      // no access token not fetching anything
      if (!accessToken) {
        logWarning(
          "While fetching feature configuration in provider access token not found skipping configuration fetch"
        );
        return undefined;
      }

      const configurationsKey = delegatedVendorName ? `${healthPlanName}::${delegatedVendorName}` : healthPlanName;

      // If already fetched, return the existing config
      const featureConfig = featureConfigurationsRecord[configurationsKey]?.[configuration];
      if (featureConfig) {
        return featureConfig as FetchFeatureConfigurationReturnType<T>;
      }

      // Else, fetch and store in context
      const fetchedConfiguration = await fetchConfiguration(undefined, {
        pathParams: {
          configuration,
        },
        queryParams: {
          featureConfigurationLevel: delegatedVendorName ? "DELEGATED_VENDOR" : "HEALTH_PLAN",
          healthPlanName,
          delegatedVendorName,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setFeatureConfigurationRecord((previousState) => {
        return {
          ...previousState,
          [configurationsKey]: {
            ...previousState[configurationsKey],
            [configuration]: { ...fetchedConfiguration },
          },
        };
      });
      return fetchedConfiguration as FetchFeatureConfigurationReturnType<T>;
    } catch (error) {
      console.error(`Not able to fetch configuration ${configuration}`);
    }
  };

  const debouncedFetchFuncs: Map<string, DebouncedFuncLeading<typeof fetchFeatureConfiguration>> = new Map();
  /**
   *  Each unique combo of config + health plan + delegated vendor has its own debounced function.
   *  This prevents identical API calls when multiple components mount at the same time.
   */
  const fetchFeatureConfigurationDebounced = <T extends FeatureConfiguration>(
    configuration: T,
    healthPlanName: string,
    delegatedVendorName?: string
  ): Promise<FetchFeatureConfigurationReturnType<T> | undefined> => {
    const key = `${configuration}-${healthPlanName}${!!delegatedVendorName ? `-${delegatedVendorName}` : ""}`;
    let debouncedFunc;

    if (debouncedFetchFuncs.has(key)) {
      debouncedFunc = debouncedFetchFuncs.get(key);
    } else {
      // Set leading: true so we start fetching ASAP
      debouncedFunc = debounce(fetchFeatureConfiguration, 1000, { leading: true, trailing: false });
      debouncedFetchFuncs.set(key, debouncedFunc);
    }

    return debouncedFunc?.(configuration, healthPlanName, delegatedVendorName) || Promise.resolve(undefined);
  };

  return (
    <FeatureConfigurationContext.Provider
      value={{
        featureConfigurations: featureConfigurationsRecord,
        fetchFeatureConfiguration: fetchFeatureConfigurationDebounced,
      }}
    >
      {children}
    </FeatureConfigurationContext.Provider>
  );
};

export const useConfiguration = <T extends keyof FeatureConfigurationsRecord>(
  configuration: T,
  healthPlanName?: string,
  delegatedVendorName?: string,
  onFinally?: () => void
) => {
  const [currentConfiguration, setCurrentConfiguration] = useState<
    FetchFeatureConfigurationReturnType<T> | undefined
  >();
  const context = useContext(FeatureConfigurationContext);
  const { fetchFeatureConfiguration } = context;

  const fetchConfig = useCallback(
    async (mounted: boolean) => {
      if (healthPlanName && fetchFeatureConfiguration) {
        try {
          const configurationFetched = await fetchFeatureConfiguration(
            configuration,
            healthPlanName,
            delegatedVendorName
          );
          if (configurationFetched && mounted) {
            setCurrentConfiguration(configurationFetched);
          }
        } finally {
          if (mounted) {
            onFinally?.();
          }
        }
      } else if (mounted) {
        setCurrentConfiguration(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configuration, healthPlanName, delegatedVendorName, onFinally]
  );

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchConfig(mounted);
    }

    return () => {
      mounted = false;
    };
  }, [fetchConfig]);

  return currentConfiguration;
};
